import { useEffect, useRef } from "react";

export type PageResumeRefetch = () => Promise<unknown>;

/** Refetch a query when the browser returns to the foreground.
 *
 * Example: `usePageResumeRefetch(query.refetch)`.
 */
export function usePageResumeRefetch(refetch: PageResumeRefetch): void {
  const lastRefetchAt = useRef(0);
  useEffect(() => {
    const refresh = (): void => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastRefetchAt.current < 50) return;
      lastRefetchAt.current = now;
      void refetch();
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    // bfcache restore (#220 hole #3): back/forward can fire `pageshow` without a
    // `visibilitychange`/`focus`, leaving the candle base stale until F5.
    window.addEventListener("pageshow", refresh);
    return () => {
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
    };
  }, [refetch]);
}
